import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import permissionService from '../../../services/permissionService';
import Table from '../../../components/Common/Table/Table';
import Heading from '../../../components/Common/Heading/Heading';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import SkeletonLoader from '../../../components/SkeletonLoader/SkeletonLoader';
import ListScreenSearchBar from '../../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import { matchesWordStartAny } from '../../../utils/searchMatch';
import './PermissionList.css';
import '../../../components/Common/ListScreenPageLayout/listScreenPage.css';

const PermissionList = () => {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await permissionService.getPermissions();

      if (response.success) {
        setPermissions(response.data || []);
      } else {
        setError(response.message || 'Failed to fetch permissions');
        toast.error(response.message || 'Failed to fetch permissions');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch permissions';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const filterPermissions = useCallback(() => {
    if (!searchTerm.trim()) {
      setFilteredPermissions(permissions);
      return;
    }

    const filtered = permissions.filter((permission) =>
      matchesWordStartAny(
        [permission.name, permission.route_name, permission.page_name],
        searchTerm
      )
    );
    setFilteredPermissions(filtered);
  }, [permissions, searchTerm]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  useEffect(() => {
    filterPermissions();
  }, [filterPermissions]);

  const handleAddPermission = () => {
    navigate('/permission/permissions/add');
  };

  const handleEditPermission = (permissionId) => {
    navigate(`/permission/permissions/edit/${permissionId}`);
  };

  const totalItems = filteredPermissions.length;
  const effectiveItemsPerPage = itemsPerPage === 9999 ? Math.max(totalItems, 1) : itemsPerPage;
  const totalPages = Math.max(1, Math.ceil(totalItems / effectiveItemsPerPage));
  const paginatedPermissions = filteredPermissions.slice(
    (currentPage - 1) * effectiveItemsPerPage,
    currentPage * effectiveItemsPerPage
  );

  const handleEntriesChange = (value) => {
    setItemsPerPage(value === 'all' ? 9999 : parseInt(value, 10));
    setCurrentPage(1);
  };

  const columns = [
    { key: 'name', title: 'Permission Name', render: (row) => row.name || 'N/A' },
    { key: 'route_name', title: 'Route Name', render: (row) => row.route_name || 'N/A' },
    { key: 'page_name', title: 'Page Name', render: (row) => row.page_name || 'N/A' },
    {
      key: 'action',
      title: 'Action',
      className: 'reusable-table-centered-col',
      render: (row) => (
        <ActionButtons
          onEdit={() => handleEditPermission(row.permission_id)}
          hideView
          hideDelete
        />
      )
    }
  ];

  return (
    <div className="permission-list-page list-screen-page-container">
      <div className="permission-list-header list-screen-top-bar">
        <div className="list-screen-title-section">
          <Heading title="Permission List" level="h2" className="permission-list-title list-screen-title-text" />
        </div>
        <div className="list-screen-actions">
          <ListScreenSearchBar
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            inputId="permission-list-search"
          />
          <button onClick={handleAddPermission} className="permission-list-add-btn list-screen-add-btn">
            <i className="fa fa-plus list-screen-add-icon" aria-hidden="true"></i> Add
          </button>
        </div>
      </div>

      {error && (
        <div className="permission-list-error-message">
          <p>{error}</p>
          <button onClick={fetchPermissions} className="permission-list-retry-btn">Retry</button>
        </div>
      )}

      <div className="permission-list-container">
        {loading ? (
          <SkeletonLoader type="mediaRows" count={2} />
        ) : (
          <Table
            columns={columns}
            data={paginatedPermissions}
            isLoading={loading}
            loadingMode="skeleton"
            skeletonRows={Math.min(effectiveItemsPerPage, 10)}
            emptyMessage={searchTerm ? 'No permissions found matching your search' : 'No permissions found'}
          />
        )}
      </div>
      {!loading && (
        <div className="permission-list-pagination-outside">
          <ListScreenPaginationBar
            entriesValue={itemsPerPage === 9999 ? 'all' : String(itemsPerPage)}
            onEntriesChange={handleEntriesChange}
            showPageSelector
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default PermissionList;

